import {
  ChatBubbleLeftIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import React, { useCallback, useMemo, useState } from 'react';

import { Conversation } from '@/types';
import { cn } from '@/utils/classname';

interface ConversationListProps {
  conversations: Conversation[];
  activeConversationId?: string | null;
  onSelectConversation: (conversationId: string) => void;
  conversationPreviews?: Record<string, string>;
}

const ConversationList: React.FC<ConversationListProps> = ({
  conversations,
  activeConversationId,
  onSelectConversation,
  conversationPreviews = {},
}) => {
  const [searchQuery, setSearchQuery] = useState('');

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) {
      return 'Just now';
    } else if (diffMins < 60) {
      return `${diffMins}m ago`;
    } else if (diffHours < 24) {
      return `${diffHours}h ago`;
    } else if (diffDays < 7) {
      return `${diffDays}d ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  // Generate conversation title with name and commit-hash style UUID
  const getConversationTitle = useCallback(
    (conversation: Conversation) => {
      const conversationId =
        conversation.uuid ||
        conversation.id ||
        (conversation.key
          ? conversation.key.replace('#conversation#', '')
          : '');

      const shortId = conversationId ? conversationId.slice(-8) : 'unknown';

      const displayName =
        conversation.name ||
        conversation.title ||
        conversation.topic ||
        conversationPreviews[conversationId] ||
        'Chat';

      return `${displayName}@${shortId}`;
    },
    [conversationPreviews]
  );

  // Filter conversations based on search query
  const filteredConversations = useMemo(() => {
    if (!searchQuery.trim()) return conversations;

    const query = searchQuery.toLowerCase();
    return conversations.filter(conversation => {
      const conversationId =
        conversation.uuid ||
        conversation.id ||
        (conversation.key
          ? conversation.key.replace('#conversation#', '')
          : '');

      // Get the same title that will be displayed
      const displayTitle = getConversationTitle(conversation);

      // Search by conversation ID or the displayed title
      return (
        conversationId?.toLowerCase().includes(query) ||
        displayTitle?.toLowerCase().includes(query)
      );
    });
  }, [conversations, searchQuery, getConversationTitle]);

  if (filteredConversations.length === 0 && searchQuery) {
    return (
      <div className="border-chariot-stroke border-b p-1 pb-2">
        <div className="relative">
          <MagnifyingGlassIcon className="text-chariot-text-tertiary absolute top-1/2 left-3 size-4 -translate-y-1/2 transform" />
          <input
            type="text"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search Conversations..."
            className="bg-chariot-transparent text-chariot-text-primary placeholder:text-chariot-text-secondary w-full rounded-md border-0 p-3 px-10 py-2 text-sm"
          />
        </div>
        <div className="p-6 text-center">
          <div className="text-chariot-text-secondary">
            <p className="text-sm">No conversations found</p>
            <p className="mt-1 text-xs opacity-75">
              Try a different search term
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (conversations.length === 0) {
    return (
      <div className="p-6 text-center">
        <div className="text-chariot-text-secondary">
          <ChatBubbleLeftIcon className="mx-auto mb-3 h-8 w-8 opacity-40" />
          <p className="text-sm">No conversations yet</p>
          <p className="mt-1 text-xs opacity-75">
            Start a new conversation to get started
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Search input */}
      <div className="border-chariot-stroke border-b p-1 pb-2">
        <div className="relative">
          <MagnifyingGlassIcon className="text-chariot-text-tertiary absolute top-1/2 left-3 size-4 -translate-y-1/2 transform" />
          <input
            type="text"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search Conversations..."
            className="bg-chariot-transparent text-chariot-text-primary placeholder:text-chariot-text-secondary w-full rounded-md border-0 p-3 px-10 py-2 text-sm"
          />
        </div>
      </div>

      {filteredConversations.map((conversation, index) => {
        // Extract conversation ID from key (format: #conversation#<uuid>)
        const conversationId =
          conversation.uuid ||
          conversation.id ||
          (conversation.key
            ? conversation.key.replace('#conversation#', '')
            : '');
        const isActive = conversationId === activeConversationId;
        const isFirst = index === 0;

        return (
          <div
            key={conversationId || conversation.key}
            className={cn(
              'group flex items-center transition-all',
              'py-2 pr-1 pl-2 text-sm',
              'cursor-pointer focus-visible:outline-none',
              !isFirst && 'border-chariot-stroke border-t',
              isActive && 'bg-chariot-stroke text-chariot-text-primary',
              'hover:shadow-[0_0_6px_2px_rgba(0,0,0,0.35)] dark:hover:shadow-[0_0_6px_2px_rgba(255,255,255,0.35)]'
            )}
            tabIndex={0}
            onClick={() => onSelectConversation(conversationId)}
          >
            <div className="flex-1 truncate">
              <p
                className={cn(
                  'truncate text-xs font-medium',
                  isActive
                    ? 'text-chariot-text-primary'
                    : 'text-chariot-text-secondary'
                )}
              >
                {conversation.topic ||
                  conversationPreviews[conversationId] ||
                  'New Conversation'}
              </p>
              <p className="text-chariot-text-tertiary mt-1 truncate text-xs">
                {formatDate(conversation.created)}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ConversationList;
